import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addSubtest } from "../reducers/ruleFrameSlice";
import { TextField, MenuItem, Box, Button } from "@mui/material";

const DANGEROUS_LEADING_CHARS = /^[=+\-@#\/\\\t\r]/;
const SUBTEST_NAME_RE = /^[A-Za-z0-9 _.()\-#\/\\*]{1,100}$/;

function validateSubtestName(rawName) {
  const name = (rawName || "").trim();

  if (!name) {
    return "Sub-test name cannot be empty";
  }

  if (DANGEROUS_LEADING_CHARS.test(name)) {
    return "Subtest cannot start with =, +, -, @, #, /, \\, or a tab/carriage return character";
  }

  if (!SUBTEST_NAME_RE.test(name)) {
    return "Subtest may only contain letters, numbers, spaces, and . _ ( ) - # / \\";
  }

  return null;
}

export default function BloodTestsManager({ categoryId }) {
  const dispatch = useDispatch();
  const { selectedCategory } = useSelector((state) => state.ruleFrame);
  const [testName, setTestName] = useState("");
  const [testNameError, setTestNameError] = useState("");
  const [minAge, setMinAge] = useState("");
  const [maxAge, setMaxAge] = useState("");
  const [gender, setGender] = useState("Both");

  const handleTestNameChange = (e) => {
    const value = e.target.value;
    setTestName(value);
    // Clear the error as soon as the user edits the field; re-validate on submit.
    if (testNameError) setTestNameError("");
  };

  const handleAdd = () => {
    const trimmed = testName.trim();
    const validationError = validateSubtestName(trimmed);
    if (validationError) {
      setTestNameError(validationError);
      return;
    }

    dispatch(
      addSubtest({
        category: categoryId,
        sub_test_name: trimmed,
        min_age: minAge,
        max_age: maxAge,
        gender,
      }),
    );
    setTestName("");
    setTestNameError("");
    setMinAge("");
    setMaxAge("");
    setGender("Both");
  };

  const isAgeInvalid =
    minAge !== "" &&
    maxAge !== "" &&
    parseInt(minAge, 10) >= parseInt(maxAge, 10);
  const isDisabled = !testName.trim() || isAgeInvalid;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
          mb: 0,
          pt: 0,
        }}
      >
        <TextField
          label={`Enter a new Category Sub-test for Category - ${selectedCategory || ""}`}
          value={testName}
          onChange={handleTestNameChange}
          error={Boolean(testNameError)}
          helperText={testNameError || " "}
          sx={{
            mt: 2.5,
            flex: 2,
            minWidth: 280,
            "& .MuiInputLabel-root": {
              top: "-16px",
            },
            "& .MuiInputBase-input": {
              fontSize: "0.85rem",
              padding: "2px 6px",
            },
          }}
        />
        <TextField
          label="Min Age"
          value={minAge}
          onChange={(e) => setMinAge(e.target.value)}
          sx={{
            flex: 2,
            maxWidth: 100,
            "& .MuiInputLabel-root": {
              top: "-16px",
            },
            "& .MuiInputBase-input": {
              fontSize: "0.85rem",
              padding: "2px 6px",
            },
          }}
        />
        <TextField
          label="Max Age"
          value={maxAge}
          onChange={(e) => setMaxAge(e.target.value)}
          sx={{
            flex: 2,
            maxWidth: 100,
            "& .MuiInputLabel-root": {
              top: "-16px",
            },
            "& .MuiInputBase-input": {
              fontSize: "0.85rem",
              padding: "2px 6px",
            },
          }}
        />
        <TextField
          select
          label="Gender"
          value={gender}
          onChange={(e) => setGender(e.target.value)}
          sx={{
            flex: 2,
            maxWidth: 120,
            "& .MuiInputLabel-root": {
              top: "-16px",
            },
            "& .MuiInputBase-input": {
              fontSize: "0.85rem",
              padding: "2px 6px",
            },
          }}
        >
          <MenuItem
            value="Male"
            sx={{ width: "100%", fontSize: "0.85rem", height: "26px" }}
          >
            Male
          </MenuItem>
          <MenuItem
            value="Female"
            sx={{ width: "100%", fontSize: "0.85rem", height: "26px" }}
          >
            Female
          </MenuItem>
          <MenuItem
            value="Both"
            sx={{ width: "100%", fontSize: "0.85rem", height: "26px" }}
          >
            Both
          </MenuItem>
        </TextField>
        <Button
          color="error"
          variant="dense"
          onClick={handleAdd}
          disabled={isDisabled}
        >
          Add
        </Button>
      </Box>
    </>
  );
}
